/**
 * RichContentBlocks — renderer for the structured content used in News,
 * Events and Forum posts.
 *
 * Each "block" is `{ id, type, data }` and is one of:
 *   text:   { html }                                          → rich text
 *   button: { label, url, style, size, align }                → styled CTA
 *   image:  { url, alt, link, align, width, caption }         → optional hyperlink
 *
 * The renderer is tolerant: unknown types are silently skipped. If `blocks`
 * is empty/undefined and `fallbackHtml` is provided, falls back to plain HTML.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, X as CloseIcon, FileText, Download } from 'lucide-react';
import { absoluteUrl } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';

const BUTTON_STYLES = {
  primary:   'bg-cmic-600 hover:bg-cmic-700 text-white shadow-sm hover:shadow',
  secondary: 'bg-white border-2 border-cmic-600 text-cmic-700 hover:bg-cmic-50',
  ghost:     'text-cmic-700 hover:bg-cmic-50',
  amber:     'bg-amber-500 hover:bg-amber-600 text-white shadow-sm',
  dark:      'bg-charcoal-800 hover:bg-charcoal-900 text-white shadow-sm',
  outline:   'border border-charcoal-300 text-charcoal-700 hover:border-cmic-500 hover:text-cmic-700',
};

const BUTTON_SIZES = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

const ALIGN_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' };

export const BUTTON_STYLE_OPTIONS = Object.keys(BUTTON_STYLES);
export const BUTTON_SIZE_OPTIONS = Object.keys(BUTTON_SIZES);

/** Sanitize URL: only http(s) and mailto, otherwise '#'. */
function safeUrl(url) {
  if (!url) return '#';
  const u = String(url).trim();
  if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
  if (u.startsWith('/')) return u;
  return `https://${u}`;
}

function isExternal(url) {
  return /^https?:\/\//i.test(url || '') && !url.includes(typeof window !== 'undefined' ? window.location.host : '');
}

function ButtonBlock({ data }) {
  const style = BUTTON_STYLES[data.style] || BUTTON_STYLES.primary;
  const size = BUTTON_SIZES[data.size] || BUTTON_SIZES.md;
  const align = ALIGN_CLASS[data.align] || ALIGN_CLASS.left;
  const url = safeUrl(data.url || '#');
  const external = isExternal(url);
  return (
    <div className={`my-5 ${align}`} data-testid="rcb-button">
      <a
        href={url}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={`inline-flex items-center gap-2 rounded-lg font-semibold transition-all ${size} ${style}`}
      >
        {data.label || 'Botón'}
        {external && <ExternalLink size={14} />}
      </a>
    </div>
  );
}

function ImageBlock({ data }) {
  if (!data.url) return null;
  const widthClass =
    data.width === 'sm' ? 'max-w-md' :
    data.width === 'md' ? 'max-w-2xl' :
    data.width === 'full' ? 'max-w-none w-full' :
    'max-w-3xl';
  const align = ALIGN_CLASS[data.align] || ALIGN_CLASS.center;
  const img = (
    <img
      src={absoluteUrl(data.url)}
      alt={data.alt || ''}
      className={`mx-auto rounded-xl shadow-sm ${widthClass} ${data.align === 'left' ? 'ml-0' : data.align === 'right' ? 'mr-0' : ''}`}
    />
  );
  const wrapped = data.link ? (
    <a href={safeUrl(data.link)} target={isExternal(data.link) ? '_blank' : undefined} rel="noopener noreferrer" className="inline-block">
      {img}
    </a>
  ) : img;
  return (
    <figure className={`my-6 ${align}`} data-testid="rcb-image">
      {wrapped}
      {data.caption && (
        <figcaption className="mt-2 text-xs text-charcoal-500 italic">{data.caption}</figcaption>
      )}
    </figure>
  );
}

function TextBlock({ data }) {
  const html = data.html || '';
  if (!html.trim()) return null;
  return (
    <div
      lang="es"
      className="prose prose-base max-w-none text-charcoal-700 prose-a:text-cmic-600 prose-strong:text-charcoal-900 prose-headings:text-charcoal-900 my-4 break-words [hyphens:auto]"
      dangerouslySetInnerHTML={{ __html: normalizeHtml(html) }}
      data-testid="rcb-text"
    />
  );
}

/**
 * VideoBlock — embeds YouTube and Vimeo videos.
 *
 * Accepts any of the common URL forms:
 *   - https://www.youtube.com/watch?v=XYZ
 *   - https://youtu.be/XYZ
 *   - https://www.youtube.com/embed/XYZ
 *   - https://vimeo.com/123456789
 *   - https://player.vimeo.com/video/123456789
 *
 * Renders nothing if the URL can't be parsed (graceful fail in production).
 */
export function parseVideoUrl(rawUrl) {
  if (!rawUrl) return null;
  const url = String(rawUrl).trim();
  // Uploaded / direct video file (served from disk) — rendered with <video>.
  if (/\/uploads\/videos\//i.test(url) || /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|#|$)/i.test(url)) {
    return { provider: 'file', id: 'archivo', src: url };
  }
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (ytMatch) {
    return { provider: 'youtube', id: ytMatch[1], embed: `https://www.youtube.com/embed/${ytMatch[1]}` };
  }
  // Vimeo
  const viMatch = url.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (viMatch) {
    return { provider: 'vimeo', id: viMatch[1], embed: `https://player.vimeo.com/video/${viMatch[1]}` };
  }
  return null;
}

function VideoBlock({ data }) {
  const parsed = parseVideoUrl(data.url);
  if (!parsed) return null;
  const align = ALIGN_CLASS[data.align] || ALIGN_CLASS.center;
  const widthClass =
    data.width === 'sm' ? 'max-w-md' :
    data.width === 'md' ? 'max-w-2xl' :
    data.width === 'full' ? 'max-w-none w-full' :
    'max-w-3xl';
  return (
    <figure className={`my-6 ${align}`} data-testid="rcb-video">
      <div className={`relative mx-auto rounded-xl overflow-hidden shadow-md bg-black ${widthClass} ${data.align === 'left' ? 'ml-0' : data.align === 'right' ? 'mr-0' : ''}`} style={{ aspectRatio: '16 / 9' }}>
        {parsed.provider === 'file' ? (
          <video
            src={absoluteUrl(parsed.src)}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <iframe
            src={parsed.embed}
            title={data.caption || `${parsed.provider} video`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 w-full h-full border-0"
          />
        )}
      </div>
      {data.caption && (
        <figcaption className="mt-2 text-xs text-charcoal-500 italic">{data.caption}</figcaption>
      )}
    </figure>
  );
}

export function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '';
  const kb = 1024;
  if (bytes < kb) return `${bytes} B`;
  if (bytes < kb * kb) return `${(bytes / kb).toFixed(1)} KB`;
  if (bytes < kb * kb * kb) return `${(bytes / (kb * kb)).toFixed(1)} MB`;
  return `${(bytes / (kb * kb * kb)).toFixed(2)} GB`;
}

const DOC_EXT_COLORS = {
  pdf:  { bg: 'bg-red-50',    border: 'border-red-200',    icon: 'text-red-600',    label: 'PDF' },
  doc:  { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'text-blue-600',   label: 'DOC' },
  docx: { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'text-blue-600',   label: 'DOCX' },
  xls:  { bg: 'bg-emerald-50',border: 'border-emerald-200',icon: 'text-emerald-600',label: 'XLS' },
  xlsx: { bg: 'bg-emerald-50',border: 'border-emerald-200',icon: 'text-emerald-600',label: 'XLSX' },
  ppt:  { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', label: 'PPT' },
  pptx: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', label: 'PPTX' },
  zip:  { bg: 'bg-violet-50', border: 'border-violet-200', icon: 'text-violet-600', label: 'ZIP' },
  txt:  { bg: 'bg-gray-50',   border: 'border-gray-200',   icon: 'text-gray-600',   label: 'TXT' },
  csv:  { bg: 'bg-emerald-50',border: 'border-emerald-200',icon: 'text-emerald-600',label: 'CSV' },
  odt:  { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'text-blue-600',   label: 'ODT' },
};

function DocumentBlock({ data }) {
  if (!data.url) return null;
  const ext = (data.extension || data.url.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const palette = DOC_EXT_COLORS[ext] || { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-charcoal-500', label: ext.toUpperCase() || 'FILE' };
  const downloadName = data.original_filename || data.filename || data.title || `documento.${ext}`;
  const size = formatBytes(data.size_bytes);
  return (
    <a
      href={absoluteUrl(data.url)}
      target="_blank"
      rel="noopener noreferrer"
      download={downloadName}
      className={`group my-5 flex items-center gap-4 p-4 sm:p-5 rounded-xl border ${palette.bg} ${palette.border} hover:shadow-md hover:-translate-y-0.5 transition-all`}
      data-testid="rcb-document"
    >
      <div className={`flex-shrink-0 w-14 h-16 rounded-lg bg-white border ${palette.border} flex flex-col items-center justify-center shadow-sm`}>
        <FileText size={22} className={palette.icon} strokeWidth={1.5} />
        <span className={`text-[9px] font-bold tracking-wide mt-0.5 ${palette.icon}`}>{palette.label}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-charcoal-800 text-sm sm:text-base leading-tight truncate">
          {data.title || downloadName}
        </p>
        {data.description && (
          <p className="text-xs text-charcoal-500 mt-1 line-clamp-2">{data.description}</p>
        )}
        <p className="text-[11px] text-charcoal-400 mt-1.5 flex items-center gap-2">
          {palette.label}{size && <> · {size}</>}
        </p>
      </div>
      <div className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border ${palette.border} ${palette.icon} text-xs font-semibold group-hover:bg-cmic-600 group-hover:text-white group-hover:border-cmic-600 transition-colors`}>
        <Download size={13} /> Descargar
      </div>
    </a>
  );
}

function GalleryCarousel({ items, onOpen, autoplay = false, intervalSeconds = 4 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((n) => setIdx((i) => (n + items.length) % items.length), [items.length]);
  const prev = useCallback(() => go(idx - 1), [go, idx]);
  const next = useCallback(() => go(idx + 1), [go, idx]);
  const current = items[idx];

  // Auto-advance when enabled, more than one image, and not paused (hover / lightbox).
  useEffect(() => {
    if (!autoplay || paused || items.length <= 1) return undefined;
    const ms = Math.min(30, Math.max(1, intervalSeconds || 4)) * 1000;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ms);
    return () => clearInterval(t);
  }, [autoplay, paused, intervalSeconds, items.length]);

  return (
    <div data-testid="rcb-gallery-carousel" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Main stage: full image (object-contain) over a blurred backdrop so
          posters/flyers are never cropped. Click opens the lightbox. */}
      <div className="relative w-full h-72 sm:h-[26rem] rounded-xl overflow-hidden bg-charcoal-900 group">
        <img
          src={absoluteUrl(current.url)}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
        />
        <button
          type="button"
          onClick={() => onOpen(idx)}
          className="absolute inset-0 w-full h-full flex items-center justify-center focus:outline-none"
          data-testid="rcb-carousel-open"
          aria-label="Ampliar imagen"
        >
          <img
            src={absoluteUrl(current.url)}
            alt={current.alt || ''}
            className="relative max-h-full max-w-full object-contain"
          />
        </button>
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              data-testid="rcb-carousel-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white text-charcoal-800 shadow-md transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="rcb-carousel-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white text-charcoal-800 shadow-md transition-colors"
              aria-label="Siguiente"
            >
              <ChevronRight size={22} />
            </button>
            <span className="absolute bottom-3 right-3 text-xs font-medium text-white bg-black/50 px-2 py-0.5 rounded-full">
              {idx + 1} / {items.length}
            </span>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {items.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="rcb-carousel-thumbs">
          {items.map((img, i) => (
            <button
              type="button"
              key={img.id || i}
              onClick={() => setIdx(i)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === idx ? 'border-cmic-500 ring-2 ring-cmic-500/20' : 'border-transparent opacity-70 hover:opacity-100'}`}
              aria-label={`Ir a la imagen ${i + 1}`}
            >
              <img src={absoluteUrl(img.url)} alt={img.alt || ''} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryBlock({ data }) {
  const items = Array.isArray(data.items) ? data.items.filter((it) => it && it.url) : [];
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const close = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(() => setLightboxIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length)), [items.length]);
  const next = useCallback(() => setLightboxIdx((i) => (i === null ? null : (i + 1) % items.length)), [items.length]);

  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIdx, close, prev, next]);

  if (items.length === 0) return null;

  const cols = data.columns === 2 ? 'grid-cols-2'
    : data.columns === 4 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
    : 'grid-cols-2 sm:grid-cols-3'; // default 3

  const current = lightboxIdx !== null ? items[lightboxIdx] : null;
  const isCarousel = data.display === 'carousel';

  return (
    <figure className="my-6" data-testid="rcb-gallery">
      {isCarousel ? (
        <GalleryCarousel items={items} onOpen={(idx) => setLightboxIdx(idx)} autoplay={!!data.autoplay} intervalSeconds={data.autoplay_seconds || 4} />
      ) : (
        <div className={`grid gap-2 sm:gap-3 ${cols}`}>
          {items.map((img, idx) => (
            <button
              type="button"
              key={img.id || idx}
              onClick={() => setLightboxIdx(idx)}
              className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cmic-500/40"
              data-testid="rcb-gallery-thumb"
            >
              <img
                src={absoluteUrl(img.url)}
                alt={img.alt || ''}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
            </button>
          ))}
        </div>
      )}
      {data.caption && (
        <figcaption className="mt-3 text-xs text-charcoal-500 italic text-center">{data.caption}</figcaption>
      )}

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8"
          onClick={close}
          data-testid="rcb-gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Cerrar"
          >
            <CloseIcon size={22} />
          </button>
          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                aria-label="Anterior"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                aria-label="Siguiente"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}
          <figure className="max-w-6xl max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={absoluteUrl(current.url)}
              alt={current.alt || ''}
              className="max-h-[80vh] max-w-full object-contain rounded"
            />
            {(current.caption || current.alt) && (
              <figcaption className="mt-3 text-sm text-white/80 italic text-center">
                {current.caption || current.alt}
              </figcaption>
            )}
            {items.length > 1 && (
              <p className="mt-2 text-xs text-white/50">{lightboxIdx + 1} / {items.length}</p>
            )}
          </figure>
        </div>
      )}
    </figure>
  );
}

export default function RichContentBlocks({ blocks, fallbackHtml = '' }) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    if (fallbackHtml && fallbackHtml.trim()) {
      return <TextBlock data={{ html: fallbackHtml }} />;
    }
    return null;
  }
  return (
    <div className="rich-content-blocks" data-testid="rich-content-blocks">
      {blocks.map((b) => {
        if (!b || !b.type) return null;
        const data = b.data || {};
        if (b.type === 'text')     return <TextBlock key={b.id} data={data} />;
        if (b.type === 'button')   return <ButtonBlock key={b.id} data={data} />;
        if (b.type === 'image')    return <ImageBlock key={b.id} data={data} />;
        if (b.type === 'video')    return <VideoBlock key={b.id} data={data} />;
        if (b.type === 'gallery')  return <GalleryBlock key={b.id} data={data} />;
        if (b.type === 'document') return <DocumentBlock key={b.id} data={data} />;
        return null;
      })}
    </div>
  );
}
